import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { Router } from '@angular/router';
import { ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PAGE_NAMES, ROLES_TYPE } from '../../../core/helpers/constants';
import { getRolesForPage } from '../../../core/helpers/utility';
import { IPermission } from '../../../core/models/auth.models';
import { ICompany } from '../../../core/models/datafeed.models';
import { DataFeedService } from '../../../shared/api/datafeed.service';
import { AppService } from '../../../store/app.service';
import { IReportingPeriod } from '../../../core/models/reporting.models';
import { ReportsApiService } from '../reports.apiservice';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { InstanceService } from '../../../core/instance/instance.service';

@Component({
  selector: 'app-reports-brand-overview',
  templateUrl: './reports-brand-overview.component.html',
  styleUrls: ['./reports-brand-overview.component.scss']
})
export class ReportsBrandOverviewComponent implements OnInit, OnDestroy {
  readonly conversionActionOptions: Array<{ key: string; labelKey: string }> = [
    { key: 'purchases', labelKey: 'PURCHASES' },
    { key: 'booking', labelKey: 'BOOKING' },
    { key: 'add_to_cart', labelKey: 'ADD_TO_CART' },
    { key: 'checkout', labelKey: 'CHECKOUT' },
    { key: 'leads', labelKey: 'LEADS' },
  ];

  doWhat: IPermission = {} as IPermission;
  companiesData: Array<ICompany> = [];
  selectedCompanyId: string = '';
  get companySelected(): boolean {
    return !!(this.reportForm?.get('companySelector')?.value ?? this.selectedCompanyId);
  }
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  isAdmin: boolean = false;
  roleOptions: any = {};
  isExternal: boolean = false;
  isCompanyOwner: boolean = false;
  loading: boolean = false;
  campaignsData: Array<any>;

  @Input() defaultReportPeriod: string = 'Previous Month';

  reportForm: FormGroup;
  reportPeriods: Array<IReportingPeriod & { displayKey: string; title: string }> = [];
  DateStart: string | null = null;
  DateStop: string | null = null;
  selectedPeriod: string | null = null;
  companyMetaData: { [key: string]: any }[] = [];
  companyMetaTotals: { [key: string]: any }[] = [];
  companyMetaAverages: { [key: string]: any }[] = [];
  companyGoogleData: { [key: string]: any; }[] = [];
  companyGoogleTotals: { [key: string]: any }[] = [];
  companyGoogleAverages: { [key: string]: any }[] = [];
  companyTempTotals: { [key: string]: number } = {
    cost: 0,
    leads: 0,
    impressions: 0,
    clicks: 0,
    cost_prev: 0,
    leads_prev: 0,
    impressions_prev: 0,
    clicks_prev: 0,
  };
  previousMetaCampaigns: number = 0;
  previousGoogleCampaigns: number = 0;
  previousCombinedCampaigns: number = 0;
  activeTab: string = 'meta';

  get hasMetaPlatformData(): boolean {
    return (this.companyMetaData?.length ?? 0) > 0;
  }

  get hasGooglePlatformData(): boolean {
    return (this.companyGoogleData?.length ?? 0) > 0;
  }

  get metaReportsPurchases(): boolean {
    return (this.companyMetaData ?? []).some(row => row?.report_purchases === true || row?.report_purchases === 'true');
  }

  get googleReportsPurchases(): boolean {
    return (this.companyGoogleData ?? []).some(row => row?.report_purchases === true || row?.report_purchases === 'true');
  }

  get combinedReportsPurchases(): boolean {
    return this.metaReportsPurchases || this.googleReportsPurchases;
  }

  get isEmbeddedContext(): boolean {
    return this.router.url.includes('/dashboard');
  }

  // -----------------------------
  // Combined totals (Meta + Google)
  // -----------------------------

  get totalCombinedCampaigns(): number {
    const ids = new Set<string>();
    for (const row of (this.companyMetaData ?? [])) {
      const id = row?.reach_campaign_id ?? row?.reachCampaignId ?? row?.campaignId ?? row?.id;
      if (id) {
        ids.add(String(id));
      }
    }
    for (const row of (this.companyGoogleData ?? [])) {
      const id = row?.reach_campaign_id ?? row?.reachCampaignId ?? row?.campaignId ?? row?.id;
      if (id) {
        ids.add(String(id));
      }
    }
    return ids.size;
  }

  // -----------------------------
  // Meta-only totals
  // -----------------------------

  get totalMetaCampaigns(): number {
    return this.countUniqueCampaigns(this.companyMetaData);
  }

  get totalMetaSpend(): number {
    return this.getMetricValue(this.companyMetaTotals, this.companyMetaData, 'cost');
  }
  get prevMetaSpend(): number {
    return this.getMetricValue(this.companyMetaTotals, this.companyMetaData, 'cost_prev');
  }

  get totalMetaLeads(): number {
    return this.getMetricValue(this.companyMetaTotals, this.companyMetaData, 'leads');
  }
  get prevMetaLeads(): number {
    return this.getMetricValue(this.companyMetaTotals, this.companyMetaData, 'leads_prev');
  }

  get totalMetaClicks(): number {
    return this.getClicksValue(this.companyMetaTotals, this.companyMetaData, false);
  }
  get prevMetaClicks(): number {
    return this.getClicksValue(this.companyMetaTotals, this.companyMetaData, true);
  }

  get totalMetaImpressions(): number {
    return this.getMetricValue(this.companyMetaTotals, this.companyMetaData, 'impressions');
  }
  get prevMetaImpressions(): number {
    return this.getMetricValue(this.companyMetaTotals, this.companyMetaData, 'impressions_prev');
  }

  get totalMetaCpl(): number {
    return this.safeDivide(this.totalMetaSpend, this.totalMetaLeads);
  }
  get prevMetaCpl(): number {
    return this.safeDivide(this.prevMetaSpend, this.prevMetaLeads);
  }

  get totalMetaCpm(): number {
    return this.safeDivide(this.totalMetaSpend * 1000, this.totalMetaImpressions);
  }
  get prevMetaCpm(): number {
    return this.safeDivide(this.prevMetaSpend * 1000, this.prevMetaImpressions);
  }

  get totalMetaCtr(): number {
    return this.safeDivide(this.totalMetaClicks, this.totalMetaImpressions);
  }
  get prevMetaCtr(): number {
    return this.getAverageMetric(this.companyMetaAverages, 'ctr_prev', this.safeDivide(this.prevMetaClicks, this.prevMetaImpressions));
  }

  get totalMetaLsr(): number {
    return this.safeDivide(this.totalMetaLeads, this.totalMetaClicks);
  }
  get prevMetaLsr(): number {
    return this.getAverageMetric(this.companyMetaAverages, 'lsr_prev', this.safeDivide(this.prevMetaLeads, this.prevMetaClicks));
  }

  get totalMetaPurchases(): number {
    return this.getMetricValue(this.companyMetaTotals, this.companyMetaData, 'purchases');
  }
  get prevMetaPurchases(): number {
    return this.getMetricValue(this.companyMetaTotals, this.companyMetaData, 'purchases_prev');
  }
  get totalMetaConversionActions(): number {
    return this.getConversionActionsValue(this.companyMetaTotals, this.companyMetaData, false);
  }
  get prevMetaConversionActions(): number {
    return this.getConversionActionsValue(this.companyMetaTotals, this.companyMetaData, true);
  }
  get totalMetaCpa(): number {
    return this.safeDivide(this.totalMetaSpend, this.totalMetaConversionActions);
  }
  get prevMetaCpa(): number {
    return this.safeDivide(this.prevMetaSpend, this.prevMetaConversionActions);
  }
  get totalMetaConvRate(): number {
    return this.safeDivide(this.totalMetaConversionActions, this.totalMetaClicks);
  }
  get prevMetaConvRate(): number {
    return this.safeDivide(this.prevMetaConversionActions, this.prevMetaClicks);
  }
  get totalMetaPsr(): number {
    return this.totalMetaConvRate;
  }
  get prevMetaPsr(): number {
    return this.prevMetaConvRate;
  }

  // -----------------------------
  // Google-only totals
  // -----------------------------

  get totalGoogleCampaigns(): number {
    return this.countUniqueCampaigns(this.companyGoogleData);
  }

  get totalGoogleSpend(): number {
    return this.getMetricValue(this.companyGoogleTotals, this.companyGoogleData, 'cost');
  }
  get prevGoogleSpend(): number {
    return this.getMetricValue(this.companyGoogleTotals, this.companyGoogleData, 'cost_prev');
  }

  get totalGoogleLeads(): number {
    return this.getMetricValue(this.companyGoogleTotals, this.companyGoogleData, 'leads');
  }
  get prevGoogleLeads(): number {
    return this.getMetricValue(this.companyGoogleTotals, this.companyGoogleData, 'leads_prev');
  }

  get totalGoogleClicks(): number {
    return this.getClicksValue(this.companyGoogleTotals, this.companyGoogleData, false);
  }
  get prevGoogleClicks(): number {
    return this.getClicksValue(this.companyGoogleTotals, this.companyGoogleData, true);
  }

  get totalGoogleImpressions(): number {
    return this.getMetricValue(this.companyGoogleTotals, this.companyGoogleData, 'impressions');
  }
  get prevGoogleImpressions(): number {
    return this.getMetricValue(this.companyGoogleTotals, this.companyGoogleData, 'impressions_prev');
  }

  get totalGoogleCpl(): number {
    return this.safeDivide(this.totalGoogleSpend, this.totalGoogleLeads);
  }
  get prevGoogleCpl(): number {
    return this.safeDivide(this.prevGoogleSpend, this.prevGoogleLeads);
  }

  get totalGoogleCpm(): number {
    return this.safeDivide(this.totalGoogleSpend * 1000, this.totalGoogleImpressions);
  }
  get prevGoogleCpm(): number {
    return this.safeDivide(this.prevGoogleSpend * 1000, this.prevGoogleImpressions);
  }

  get totalGoogleCpc(): number {
    return this.safeDivide(this.totalGoogleSpend, this.totalGoogleClicks);
  }
  get prevGoogleCpc(): number {
    return this.getAverageMetric(this.companyGoogleAverages, 'cpc_prev', this.safeDivide(this.prevGoogleSpend, this.prevGoogleClicks));
  }

  get totalGoogleCtr(): number {
    return this.safeDivide(this.totalGoogleClicks, this.totalGoogleImpressions);
  }
  get prevGoogleCtr(): number {
    return this.getAverageMetric(this.companyGoogleAverages, 'ctr_prev', this.safeDivide(this.prevGoogleClicks, this.prevGoogleImpressions));
  }

  get totalGoogleLsr(): number {
    return this.safeDivide(this.totalGoogleLeads, this.totalGoogleClicks);
  }
  get prevGoogleLsr(): number {
    return this.getAverageMetric(this.companyGoogleAverages, 'lsr_prev', this.safeDivide(this.prevGoogleLeads, this.prevGoogleClicks));
  }

  get totalGooglePurchases(): number {
    return this.getMetricValue(this.companyGoogleTotals, this.companyGoogleData, 'purchases');
  }
  get prevGooglePurchases(): number {
    return this.getMetricValue(this.companyGoogleTotals, this.companyGoogleData, 'purchases_prev');
  }
  get totalGoogleConversionActions(): number {
    return this.getConversionActionsValue(this.companyGoogleTotals, this.companyGoogleData, false);
  }
  get prevGoogleConversionActions(): number {
    return this.getConversionActionsValue(this.companyGoogleTotals, this.companyGoogleData, true);
  }
  get totalGoogleCpa(): number {
    return this.safeDivide(this.totalGoogleSpend, this.totalGoogleConversionActions);
  }
  get prevGoogleCpa(): number {
    return this.safeDivide(this.prevGoogleSpend, this.prevGoogleConversionActions);
  }
  get totalGoogleConvRate(): number {
    return this.safeDivide(this.totalGoogleConversionActions, this.totalGoogleClicks);
  }
  get prevGoogleConvRate(): number {
    return this.safeDivide(this.prevGoogleConversionActions, this.prevGoogleClicks);
  }
  get totalGooglePsr(): number {
    return this.totalGoogleConvRate;
  }
  get prevGooglePsr(): number {
    return this.prevGoogleConvRate;
  }

  // Spend
  get totalCombinedSpend(): number {
    return this.getMetricValue(this.companyMetaTotals, this.companyMetaData, 'cost')
      + this.getMetricValue(this.companyGoogleTotals, this.companyGoogleData, 'cost');
  }
  get prevCombinedSpend(): number {
    return this.getMetricValue(this.companyMetaTotals, this.companyMetaData, 'cost_prev')
      + this.getMetricValue(this.companyGoogleTotals, this.companyGoogleData, 'cost_prev');
  }

  // Leads
  get totalCombinedLeads(): number {
    return this.getMetricValue(this.companyMetaTotals, this.companyMetaData, 'leads')
      + this.getMetricValue(this.companyGoogleTotals, this.companyGoogleData, 'leads');
  }
  get prevCombinedLeads(): number {
    return this.getMetricValue(this.companyMetaTotals, this.companyMetaData, 'leads_prev')
      + this.getMetricValue(this.companyGoogleTotals, this.companyGoogleData, 'leads_prev');
  }

  // Clicks (base metric for CTR/LSR)
  get totalCombinedClicks(): number {
    return this.getClicksValue(this.companyMetaTotals, this.companyMetaData, false)
      + this.getClicksValue(this.companyGoogleTotals, this.companyGoogleData, false);
  }
  get prevCombinedClicks(): number {
    return this.getClicksValue(this.companyMetaTotals, this.companyMetaData, true)
      + this.getClicksValue(this.companyGoogleTotals, this.companyGoogleData, true);
  }

  // Impressions (base metric for CPM/CTR)
  get totalCombinedImpressions(): number {
    return this.getMetricValue(this.companyMetaTotals, this.companyMetaData, 'impressions')
      + this.getMetricValue(this.companyGoogleTotals, this.companyGoogleData, 'impressions');
  }
  get prevCombinedImpressions(): number {
    return this.getMetricValue(this.companyMetaTotals, this.companyMetaData, 'impressions_prev')
      + this.getMetricValue(this.companyGoogleTotals, this.companyGoogleData, 'impressions_prev');
  }

  // Cost Per Lead
  get totalCombinedCpl(): number {
    return this.safeDivide(this.totalCombinedSpend, this.totalCombinedLeads);
  }
  get prevCombinedCpl(): number {
    return this.safeDivide(this.prevCombinedSpend, this.prevCombinedLeads);
  }

  // CPM
  get totalCombinedCpm(): number {
    return this.safeDivide(this.totalCombinedSpend * 1000, this.totalCombinedImpressions);
  }
  get prevCombinedCpm(): number {
    return this.safeDivide(this.prevCombinedSpend * 1000, this.prevCombinedImpressions);
  }

  // CTR
  get totalCombinedCtr(): number {
    return this.safeDivide(this.totalCombinedClicks, this.totalCombinedImpressions);
  }
  get prevCombinedCtr(): number {
    return this.safeDivide(this.prevCombinedClicks, this.prevCombinedImpressions);
  }

  // Lead Submission Rate
  get totalCombinedLsr(): number {
    // Leads per click (conversion rate)
    return this.safeDivide(this.totalCombinedLeads, this.totalCombinedClicks);
  }
  get prevCombinedLsr(): number {
    return this.safeDivide(this.prevCombinedLeads, this.prevCombinedClicks);
  }

  // Purchases
  get totalCombinedPurchases(): number {
    return this.getMetricValue(this.companyMetaTotals, this.companyMetaData, 'purchases')
      + this.getMetricValue(this.companyGoogleTotals, this.companyGoogleData, 'purchases');
  }
  get prevCombinedPurchases(): number {
    return this.getMetricValue(this.companyMetaTotals, this.companyMetaData, 'purchases_prev')
      + this.getMetricValue(this.companyGoogleTotals, this.companyGoogleData, 'purchases_prev');
  }
  get totalCombinedConversionActions(): number {
    return this.totalMetaConversionActions + this.totalGoogleConversionActions;
  }
  get prevCombinedConversionActions(): number {
    return this.prevMetaConversionActions + this.prevGoogleConversionActions;
  }
  // Cost Per Action
  get totalCombinedCpa(): number {
    return this.safeDivide(this.totalCombinedSpend, this.totalCombinedConversionActions);
  }
  get prevCombinedCpa(): number {
    return this.safeDivide(this.prevCombinedSpend, this.prevCombinedConversionActions);
  }
  // Conversion Rate
  get totalCombinedConvRate(): number {
    return this.safeDivide(this.totalCombinedConversionActions, this.totalCombinedClicks);
  }
  get prevCombinedConvRate(): number {
    return this.safeDivide(this.prevCombinedConversionActions, this.prevCombinedClicks);
  }
  // Purchase / Conversion Rate
  get totalCombinedPsr(): number {
    return this.totalCombinedConvRate;
  }
  get prevCombinedPsr(): number {
    return this.prevCombinedConvRate;
  }

  // (Impressions are still computed as a base metric for CPM/CTR, but are not displayed as a card.)

  private safeDivide(numerator: number, denominator: number): number {
    if (!Number.isFinite(numerator) || !Number.isFinite(denominator) || denominator === 0) {
      return 0;
    }
    return numerator / denominator;
  }

  getDeltaInfo(
    current: number,
    previous: number,
    invertColor: boolean = false,
  ): { direction: 'up' | 'down' | 'flat'; pct: number | null; className: string } {
    const currentValue = Number.isFinite(current) ? current : 0;
    const previousValue = Number.isFinite(previous) ? previous : 0;

    let direction: 'up' | 'down' | 'flat' = 'flat';
    if (currentValue > previousValue) {
      direction = 'up';
    } else if (currentValue < previousValue) {
      direction = 'down';
    }

    let pct: number | null = null;
    if (previousValue === 0) {
      pct = currentValue === 0 ? 0 : null;
    } else {
      pct = (currentValue - previousValue) / Math.abs(previousValue);
    }

    // If the displayed percent rounds to 0% (we render with `percent:'1.0-0'`),
    // show a dash instead of an up/down arrow.
    if (pct !== null && Math.round(Math.abs(pct) * 100) === 0) {
      return { direction: 'flat', pct: 0, className: 'text-muted' };
    }

    if (direction === 'flat') {
      return { direction, pct, className: 'text-muted' };
    }

    const isGood = invertColor ? direction === 'down' : direction === 'up';
    return { direction, pct, className: isGood ? 'text-success' : 'text-danger' };
  }

  private toNumber(raw: any): number {
    if (raw === null || raw === undefined) {
      return 0;
    }
    if (typeof raw === 'string') {
      const trimmed = raw.trim();
      if (trimmed === '') {
        return 0;
      }
      const value = Number(trimmed.replace(/,/g, ''));
      return Number.isFinite(value) ? value : 0;
    }

    const value = Number(raw);
    return Number.isFinite(value) ? value : 0;
  }

  private getTotalsRow(totals: Array<{ [key: string]: any }>): { [key: string]: any } | undefined {
    return totals?.[0];
  }

  private getAverageMetric(
    averages: Array<{ [key: string]: any }>,
    key: string,
    fallback: number,
  ): number {
    const averageRow = averages?.[0];
    return averageRow && key in averageRow ? this.toNumber(averageRow[key]) : fallback;
  }

  private getSelectedConversionActionKeys(): string[] {
    const selections = this.reportForm?.get('conversionActionSelections')?.value ?? {};
    const selectedKeys = this.conversionActionOptions
      .filter(option => selections?.[option.key] !== false)
      .map(option => option.key);

    return selectedKeys.length > 0
      ? selectedKeys
      : this.conversionActionOptions.map(option => option.key);
  }

  private hasConversionActionData(
    rows: Array<{ [key: string]: any }>,
    totals: Array<{ [key: string]: any }>,
    actionKey: string,
  ): boolean {
    const suffixes = ['', '_prev'];
    for (const suffix of suffixes) {
      const totalsRow = this.getTotalsRow(totals);
      if (totalsRow && this.toNumber(totalsRow[`${actionKey}${suffix}`]) > 0) {
        return true;
      }

      if ((rows ?? []).some(row => this.toNumber(row?.[`${actionKey}${suffix}`]) > 0)) {
        return true;
      }
    }

    return false;
  }

  private initializeConversionActionSelections(): void {
    const selectionsControl = this.reportForm?.get('conversionActionSelections');
    if (!selectionsControl) {
      return;
    }

    const combinedRows = [...(this.companyMetaData ?? []), ...(this.companyGoogleData ?? [])];
    const combinedTotals = [...(this.companyMetaTotals ?? []), ...(this.companyGoogleTotals ?? [])];
    const nextSelections = this.conversionActionOptions.reduce((acc, option) => {
      acc[option.key] = this.hasConversionActionData(combinedRows, combinedTotals, option.key);
      return acc;
    }, {} as Record<string, boolean>);

    const controls = selectionsControl as FormGroup;
    controls.patchValue(nextSelections, { emitEvent: false });
  }

  private isPurchaseReportingEnabled(
    rows: Array<{ [key: string]: any }>,
    totals: Array<{ [key: string]: any }>,
  ): boolean {
    return (rows ?? []).some(row => row?.report_purchases === true || row?.report_purchases === 'true')
      || this.getTotalsRow(totals)?.report_purchases === true
      || this.getTotalsRow(totals)?.report_purchases === 'true';
  }

  /**
   * Gets a numeric metric from the API "Total" row.
   * Falls back to summing campaign rows only when the totals row doesn't include the key.
   */
  private getMetricValue(
    totals: Array<{ [key: string]: any }>,
    rows: Array<{ [key: string]: any }>,
    key: string,
  ): number {
    const totalsRow = this.getTotalsRow(totals);
    if (totalsRow && key in totalsRow) {
      return this.toNumber(totalsRow[key]);
    }

    if (!rows?.length) {
      return 0;
    }

    let sum = 0;
    for (const row of rows) {
      sum += this.toNumber((row as any)?.[key]);
    }
    return sum;
  }

  private getConversionActionsValue(
    totals: Array<{ [key: string]: any }>,
    rows: Array<{ [key: string]: any }>,
    usePrev: boolean,
  ): number {
    if (this.isPurchaseReportingEnabled(rows, totals)) {
      return this.sumConversionActionsFromRows(rows, usePrev);
    }

    const key = usePrev ? 'conversion_actions_prev' : 'conversion_actions';
    const totalsRow = this.getTotalsRow(totals);
    if (totalsRow && key in totalsRow) {
      return this.toNumber(totalsRow[key]);
    }

    const suffix = usePrev ? '_prev' : '';
    return ['purchases', 'booking', 'add_to_cart', 'checkout', 'leads']
      .reduce((sum, actionKey) => sum + this.getMetricValue(totals, rows, `${actionKey}${suffix}`), 0);
  }

  private getRowConversionActionsValue(row: { [key: string]: any }, usePrev: boolean): number {
    if (!row) {
      return 0;
    }

    const suffix = usePrev ? '_prev' : '';
    const selectedKeys = this.getSelectedConversionActionKeys();
    return selectedKeys
      .reduce((sum, actionKey) => sum + this.toNumber(row[`${actionKey}${suffix}`]), 0);
  }

  private sumConversionActionsFromRows(rows: Array<{ [key: string]: any }>, usePrev: boolean): number {
    if (!rows?.length) {
      return 0;
    }

    return rows.reduce((sum, row) => sum + this.getRowConversionActionsValue(row, usePrev), 0);
  }

  private normalizePurchaseSummaryRows(
    rows: Array<{ [key: string]: any }>,
    totals: Array<{ [key: string]: any }>,
    averages: Array<{ [key: string]: any }>,
  ): void {
    if (!this.isPurchaseReportingEnabled(rows, totals)) {
      return;
    }

    const totalRow = this.getTotalsRow(totals);
    if (totalRow) {
      const totalConversionActions = this.sumConversionActionsFromRows(rows, false);
      const prevConversionActions = this.sumConversionActionsFromRows(rows, true);
      const totalClicks = this.toNumber(totalRow.clicks);
      const prevClicks = this.toNumber(totalRow.clicks_prev);

      totalRow.conversion_actions = totalConversionActions;
      totalRow.conversion_actions_prev = prevConversionActions;
      totalRow.cpa = this.safeDivide(this.toNumber(totalRow.cost), totalConversionActions);
      totalRow.cpa_prev = this.safeDivide(this.toNumber(totalRow.cost_prev), prevConversionActions);
      totalRow.convRate = this.safeDivide(totalConversionActions, totalClicks);
      totalRow.convRate_prev = this.safeDivide(prevConversionActions, prevClicks);
      totalRow.psr = totalRow.convRate;
      totalRow.psr_prev = totalRow.convRate_prev;
    }

    const averageRow = averages?.[0];
    if (averageRow) {
      const rowCount = rows?.length ?? 0;
      if (rowCount > 0) {
        const totalConversionActions = this.sumConversionActionsFromRows(rows, false);
        const prevConversionActions = this.sumConversionActionsFromRows(rows, true);
        const avgConversionActions = totalConversionActions / rowCount;
        const avgPrevConversionActions = prevConversionActions / rowCount;

        averageRow.conversion_actions = avgConversionActions;
        averageRow.conversion_actions_prev = avgPrevConversionActions;
        averageRow.cpa = this.safeDivide(this.toNumber(averageRow.cost), avgConversionActions);
        averageRow.cpa_prev = this.safeDivide(this.toNumber(averageRow.cost_prev), avgPrevConversionActions);
        averageRow.convRate = this.safeDivide(avgConversionActions, this.toNumber(averageRow.clicks));
        averageRow.convRate_prev = this.safeDivide(avgPrevConversionActions, this.toNumber(averageRow.clicks_prev));
        averageRow.psr = averageRow.convRate;
        averageRow.psr_prev = averageRow.convRate_prev;
      }
    }

    for (const row of rows ?? []) {
      const conversionActions = this.getRowConversionActionsValue(row, false);
      const prevConversionActions = this.getRowConversionActionsValue(row, true);
      const clicks = this.toNumber(row.clicks);
      const prevClicks = this.toNumber(row.clicks_prev);
      const cost = this.toNumber(row.cost);
      const prevCost = this.toNumber(row.cost_prev);

      row.conversion_actions = conversionActions;
      row.conversion_actions_prev = prevConversionActions;
      row.cpa = this.safeDivide(cost, conversionActions);
      row.cpa_prev = this.safeDivide(prevCost, prevConversionActions);
      row.convRate = this.safeDivide(conversionActions, clicks);
      row.convRate_prev = this.safeDivide(prevConversionActions, prevClicks);
      row.psr = row.convRate;
      row.psr_prev = row.convRate_prev;
    }
  }

  private applyConversionActionSelection(): void {
    this.normalizePurchaseSummaryRows(this.companyMetaData, this.companyMetaTotals, this.companyMetaAverages);
    this.normalizePurchaseSummaryRows(this.companyGoogleData, this.companyGoogleTotals, this.companyGoogleAverages);

    this.companyMetaData = [...this.companyMetaData];
    this.companyMetaTotals = [...this.companyMetaTotals];
    this.companyMetaAverages = [...this.companyMetaAverages];
    this.companyGoogleData = [...this.companyGoogleData];
    this.companyGoogleTotals = [...this.companyGoogleTotals];
    this.companyGoogleAverages = [...this.companyGoogleAverages];
  }

  private getClicksValue(
    totals: Array<{ [key: string]: any }>,
    rows: Array<{ [key: string]: any }>,
    usePrev: boolean,
  ): number {
    const key = usePrev ? 'clicks_prev' : 'clicks';
    const totalsRow = this.getTotalsRow(totals);

    if (totalsRow && key in totalsRow) {
      const totalsValue = this.toNumber(totalsRow[key]);

      // Some payloads reliably return `clicks_prev: 0` even though row-level values exist.
      // In that case, derive a better number from the campaign rows.
      if (usePrev && totalsValue === 0) {
        const derived = this.sumClicksFromRows(rows, true);
        return derived > 0 ? derived : 0;
      }

      return totalsValue;
    }

    return this.sumClicksFromRows(rows, usePrev);
  }

  private countUniqueCampaigns(rows: Array<{ [key: string]: any }>): number {
    const ids = new Set<string>();
    for (const row of (rows ?? [])) {
      const id = row?.reach_campaign_id ?? row?.reachCampaignId ?? row?.campaignId ?? row?.id;
      if (id) {
        ids.add(String(id));
      }
    }
    return ids.size;
  }

  private sumClicksFromRows(rows: Array<{ [key: string]: any }>, usePrev: boolean): number {
    if (!rows?.length) {
      return 0;
    }

    const clickKey = usePrev ? 'clicks_prev' : 'clicks';
    const ctrKey = usePrev ? 'ctr_prev' : 'ctr';
    const impressionsKey = usePrev ? 'impressions_prev' : 'impressions';

    let sum = 0;

    for (const row of rows) {
      // 1) Prefer explicit click fields
      if (clickKey in (row ?? {})) {
        const explicit = this.toNumber((row as any)[clickKey]);
        if (explicit > 0) {
          sum += explicit;
          continue;
        }
      }

      // 2) Fallback: derive clicks from ctr * impressions
      let ctr = this.toNumber((row as any)?.[ctrKey]);
      const impressions = this.toNumber((row as any)?.[impressionsKey]);

      // Normalize CTR if it comes back as a percent (e.g. 1.23 => 0.0123)
      if (ctr > 1 && ctr <= 100) {
        ctr = ctr / 100;
      }

      if (ctr > 0 && impressions > 0) {
        sum += ctr * impressions;
      }
    }

    return sum;
  }

  defaultColumns: Array<any> = [];

  defaultAfterColumns = [
    { title: 'CTR', field: 'ctr', sortable: true, pipe: 'PercentPipe', hoverValue: 'ctr_prev' },
    { title: 'CTR_CHANGE', field: 'ctr_compare', sortable: true, pipe: 'PercentPipe', statusClass: 'ctr_updown' },
    { title: 'LEADS', field: 'leads', sortable: true, pipe: 'NumberPipe', hoverValue: 'leads_prev' },
    { title: 'LEADS_CHANGE', field: 'leads_compare', sortable: true, pipe: 'PercentPipe', statusClass: 'leads_updown' },
    { title: 'LSR', field: 'lsr', sortable: true, pipe: 'PercentPipe', hoverValue: 'lsr_prev' },
    { title: 'LSR_CHANGE', field: 'lsr_compare', sortable: true, pipe: 'PercentPipe', statusClass: 'lsr_updown' },
    { title: 'CPL', field: 'cpl', sortable: true, pipe: 'Currency', pipeArgs: ['USD', 'symbol', '1.2-2'], statusClass: 'redalert' },
    { title: 'PREVIOUS_CPL', field: 'cpl_prev', sortable: true, pipe: 'Currency', pipeArgs: ['USD', 'symbol', '1.2-2'] },
    { title: 'CPL_CHANGE', field: 'cpl_compare', sortable: true, pipe: 'PercentPipe', statusClass: 'cpl_updown' },
    // { title: 'DMS', field: 'dms', sortable: true, searchable: true },
  ];

  defaultGoogleColumns: Array<any> = [];
  defaultMetaColumns: Array<any> = [];

  defaultPageSize = 10;

  constructor(
    private router: Router,
    private appService: AppService, 
    private reportsApiService: ReportsApiService,
    private fb: FormBuilder,
    private dataFeedService: DataFeedService,
    private translate: TranslateService,
    private instance: InstanceService,
  ) {
    this.reportForm = this.fb.group({
      key: [null, [Validators.required]],
      startDate: [null, [Validators.required]],
      stopDate: [null, [Validators.required]],
      companySelector: [null, [Validators.required]],
      conversionActionSelections: this.fb.group({
        purchases: [true],
        booking: [true],
        add_to_cart: [true],
        checkout: [true],
        leads: [true],
      }),
    });
    this.campaignsData = new Array<any>();

    this.reportForm.get('conversionActionSelections')
      ?.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.applyConversionActionSelection();
      });
  }

  ngOnInit(): void {
    if (!this.appService.getPageTitle$().getValue()) {
      this.appService.setPageTitle$('REPORTS');
    }

    this.defaultColumns = [
      ...(this.instance.isAltLayout ? [{ title: 'CAMPAIGN', field: 'location', sortable: true, anchorLink: true, anchorType: 'campaign', anchorParamsField: 'reach_campaign_id', openInNewTab: true, searchable: true }] : [{ title: 'LOCATION', field: 'location', sortable: true, anchorLink: true, anchorType: 'campaign', anchorParamsField: 'reach_campaign_id', openInNewTab: true, searchable: true }]),
      ...(this.instance.isAltLayout ? [{ title: 'LOCATION', field: 'client_name', sortable: true, anchorLink: true, anchorType: 'client', anchorParamsField: 'reach_client_id', openInNewTab: true, searchable: true }] : [{ title: 'OWNER', field: 'client_name', sortable: true, anchorLink: true, anchorType: 'client', anchorParamsField: 'reach_client_id', openInNewTab: true, searchable: true }]),
      ...(!this.instance.isAltLayout ? [{ title: 'TYPE', field: 'campaign_type', sortable: true, searchable: true }] : []),
      ...(!this.instance.isAltLayout ? [{ title: 'STAGE', field: 'campaign_stage', sortable: true, searchable: true }] : []),
      { title: 'MONTHLY_BUDGET', field: 'budget', sortable: true, pipe: 'Currency', pipeArgs: ['USD', 'symbol', '1.0-0'] },
      { title: 'AD_SPEND', field: 'cost', sortable: true, pipe: 'Currency', pipeArgs: ['USD', 'symbol', '1.2-2'], hoverValue: 'cost_prev' },
      { title: 'SPEND_CHANGE', field: 'cost_compare', sortable: true, pipe: 'PercentPipe', statusClass: 'cost_updown' },
    ];

    this.rebuildPlatformColumns();

    this.doWhat = this.appService.getUserRolesForPage(PAGE_NAMES.Reports);

    this.appService.getActiveUser$()
      .pipe(takeUntil(this.destroyed$))
      .subscribe(u => {
        this.isExternal = u.type === ROLES_TYPE.External;
        this.isCompanyOwner = u.roles.some(r => r.code === 5);
        const r = getRolesForPage(u.roles, PAGE_NAMES.Reports);
        this.isAdmin = r.isAdmin;
        this.roleOptions = r.options ? JSON.parse(r.options ?? '{}') : {};
      });

    this.getAssociatedCompanies(); 
    this.getSupportedReportingPeriods().then(() => {
      this.setDefaultReportingPeriod();
    });

    // Force ng-select to refresh selected label on language change.
    this.translate.onLangChange
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.refreshReportPeriodTitles();
      });

    // Also refresh after translation files are loaded/updated for the current language.
    this.translate.onTranslationChange
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.refreshReportPeriodTitles();
      });
    // console.log('this.campaignsData', this.campaignsData);
  }

  private rebuildPlatformColumns(): void {
    const googlePurchaseAfterColumns = this.buildPurchaseAfterColumns(this.companyGoogleTotals, this.companyGoogleData);
    const metaPurchaseAfterColumns = this.buildPurchaseAfterColumns(this.companyMetaTotals, this.companyMetaData);

    this.defaultGoogleColumns = [
      ...this.defaultColumns,
      { title: 'IMPR', field: 'impressions', sortable: true, pipe: 'NumberPipe', hoverValue: 'impressions_prev' },
      { title: 'IMPR_CHANGE', field: 'impressions_compare', sortable: true, pipe: 'PercentPipe', statusClass: 'impressions_updown' },
      { title: 'CPC', field: 'cpc', sortable: true, pipe: 'Currency', pipeArgs: ['USD', 'symbol', '1.2-2'], hoverValue: 'cpc_prev' },
      { title: 'CPC_CHANGE', field: 'cpc_compare', sortable: true, pipe: 'PercentPipe', statusClass: 'cpc_updown' },
      ...(this.googleReportsPurchases ? googlePurchaseAfterColumns : this.defaultAfterColumns)
    ];

    this.defaultMetaColumns = [
      ...this.defaultColumns,
      { title: 'CPM', field: 'cpm', sortable: true, pipe: 'Currency', pipeArgs: ['USD', 'symbol', '1.2-2'], hoverValue: 'cpm_prev' },
      { title: 'CPM_CHANGE', field: 'cpm_compare', sortable: true, pipe: 'PercentPipe', statusClass: 'cpm_updown' },
      ...(this.metaReportsPurchases ? metaPurchaseAfterColumns : this.defaultAfterColumns)
    ];
  }

  private buildPurchaseAfterColumns(
    totals: Array<{ [key: string]: any }>,
    rows: Array<{ [key: string]: any }>,
  ): Array<any> {
    const columns: Array<any> = [
      { title: 'CTR', field: 'ctr', sortable: true, pipe: 'PercentPipe', hoverValue: 'ctr_prev' },
      { title: 'CTR_CHANGE', field: 'ctr_compare', sortable: true, pipe: 'PercentPipe', statusClass: 'ctr_updown' },
    ];

    columns.push(
      { title: 'CONV_ACTIONS', field: 'conversion_actions', sortable: true, pipe: 'NumberPipe', hoverValue: 'conversion_actions_prev' },
      { title: 'CONV_CHANGE', field: 'conversion_actions_compare', sortable: true, pipe: 'PercentPipe', statusClass: 'conversion_actions_updown' },
      { title: 'CONV_RATE', field: 'convRate', sortable: true, pipe: 'PercentPipe', hoverValue: 'convRate_prev' },
      { title: 'CONV_RATE_CHANGE', field: 'convRate_compare', sortable: true, pipe: 'PercentPipe', statusClass: 'convRate_updown' },
      { title: 'CPA', field: 'cpa', sortable: true, pipe: 'Currency', pipeArgs: ['USD', 'symbol', '1.2-2'] },
      { title: 'PREVIOUS_CPA', field: 'cpa_prev', sortable: true, pipe: 'Currency', pipeArgs: ['USD', 'symbol', '1.2-2'] },
      { title: 'CPA_CHANGE', field: 'cpa_compare', sortable: true, pipe: 'PercentPipe', statusClass: 'cpa_updown' },
    );

    for (const action of [
      { title: 'PURCH', field: 'purchases', changeTitle: 'PURCHASES_CHANGE', changeField: 'purchases_compare', statusClass: 'purchases_updown' },
      { title: 'BOOK', field: 'booking', changeTitle: 'BOOKING_CHANGE', changeField: 'booking_compare', statusClass: 'booking_updown' },
      { title: 'ADD_TO_CART', field: 'add_to_cart', changeTitle: 'ADD_TO_CART_CHANGE', changeField: 'add_to_cart_compare', statusClass: 'add_to_cart_updown' },
      { title: 'CHECKOUT', field: 'checkout', changeTitle: 'CHECKOUT_CHANGE', changeField: 'checkout_compare', statusClass: 'checkout_updown' },
      { title: 'LEADS', field: 'leads', changeTitle: 'LEADS_CHANGE', changeField: 'leads_compare', statusClass: 'leads_updown' },
    ]) {
      if (!this.hasMetricData(totals, rows, action.field)) {
        continue;
      }

      columns.push(
        { title: action.title, field: action.field, sortable: true, pipe: 'NumberPipe', hoverValue: `${action.field}_prev` },
        // { title: action.changeTitle, field: action.changeField, sortable: true, pipe: 'PercentPipe', statusClass: action.statusClass },
      );
    }

    return columns;
  }

  private hasMetricData(
    totals: Array<{ [key: string]: any }>,
    rows: Array<{ [key: string]: any }>,
    key: string,
  ): boolean {
    const totalsRow = this.getTotalsRow(totals);
    if (totalsRow && this.toNumber(totalsRow[key]) > 0) {
      return true;
    }

    return (rows ?? []).some(row => this.toNumber(row?.[key]) > 0);
  }

  setDefaultReportingPeriod() {
    const defaultPeriod = this.reportPeriods.find(period => period.key === this.defaultReportPeriod);
    if (defaultPeriod) {
      this.DateStart = defaultPeriod.startDate;
      this.DateStop = defaultPeriod.stopDate;
      this.selectedPeriod = defaultPeriod.key ?? null;

      this.reportForm.patchValue({
        key: this.selectedPeriod, // This sets the default report period as selected
        startDate: this.DateStart,
        stopDate: this.DateStop,
      });
    }
  }

  getSupportedReportingPeriods(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.reportsApiService.getSupportedReportingPeriods()
        .pipe(takeUntil(this.destroyed$))
        .subscribe((response: Array<IReportingPeriod>) => {
          this.reportPeriods = (response ?? []).map(p => {
            const displayKey = this.toReportingPeriodDisplayKey(p?.key);
            return {
              ...p,
              displayKey,
              title: displayKey ? this.translate.instant(displayKey) : (p.key ?? '')
            };
          });
          resolve();
        }, reject);
    });
  }

  private refreshReportPeriodTitles(): void {
    this.reportPeriods = (this.reportPeriods ?? []).map(p => ({
      ...p,
      title: p.displayKey ? this.translate.instant(p.displayKey) : (p.key ?? '')
    }));
  }
  onReportPeriodChange(selected: any) {
    const selectedKey = typeof selected === 'string'
      ? selected
      : (selected?.key ?? selected?.[0]?.key ?? '');

    if (!selectedKey) {
      return;
    }

    const period = this.reportPeriods.find(p => p.key === selectedKey);

    if (selectedKey === 'Custom') {
      this.reportForm.patchValue({
        key: selectedKey
      });
      return;
    }

    this.DateStart = period?.startDate as string;
    this.DateStop = period?.stopDate as string;

    this.reportForm.patchValue({
      key: selectedKey,
      startDate: this.DateStart,
      stopDate: this.DateStop,
    });
  }
  get selectedReportPeriod(): string | undefined | null { return this.reportForm?.controls?.key?.value; }
  get selectedReportPeriodDisplayKey(): string {
    const key = this.selectedReportPeriod ?? '';
    if (!key) {
      return '';
    }

    return this.reportPeriods.find(p => p.key === key)?.displayKey
      ?? this.toReportingPeriodDisplayKey(key)
      ?? '';
  }

  get selectedReportPeriodTitle(): string {
    const key = this.selectedReportPeriod ?? '';
    if (!key) {
      return '';
    }

    const match = this.reportPeriods.find(p => p.key === key);
    if (match?.title) {
      return match.title;
    }

    const displayKey = match?.displayKey ?? this.toReportingPeriodDisplayKey(key);
    return displayKey ? this.translate.instant(displayKey) : key;
  }

  get datesReadonly(): boolean {
    return false;
  }

  private setCustomPeriodForManualDates(startDate: string | null, stopDate: string | null): void {
    const selectedKey = this.reportForm.get('key')?.value;
    if (!selectedKey || this.toReportingPeriodCode(selectedKey) === 'CUSTOM') {
      return;
    }

    const selectedPeriod = this.reportPeriods.find(period => period.key === selectedKey);
    if (selectedPeriod?.startDate !== startDate || selectedPeriod?.stopDate !== stopDate) {
      this.reportForm.patchValue({ key: 'Custom' });
    }
  }

  private toReportingPeriodCode(key?: string | null): string {
    const raw = (key ?? '').trim();
    if (!raw) {
      return '';
    }

    // Normalize any human label or enum-like key to something that matches i18n keys.
    const normalized = raw
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');

    const aliasMap: Record<string, string> = {
      THIS_WEEK: 'CURRENT_WEEK',
      THIS_MONTH: 'CURRENT_MONTH',
      THIS_YEAR: 'CURRENT_YEAR',
      LAST_WEEK: 'PAST_WEEK',
      LAST_30_DAYS: 'PAST_30_DAYS',
      PREVIOUS_30_DAYS: 'PAST_30_DAYS',
      LAST_MONTH: 'PREVIOUS_MONTH',
      LAST_YEAR: 'PREVIOUS_YEAR'
    };

    return aliasMap[normalized] ?? normalized;
  }

  private toReportingPeriodDisplayKey(key?: string | null): string {
    const code = this.toReportingPeriodCode(key);
    return code ? `REPORTING_PERIOD.${code}` : '';
  }

  getAssociatedCompanies() {
    if (this.isExternal) {
      this.dataFeedService.getAssociatedCompanies()
        .subscribe(res => {
          // Remove duplicate companies based on id
          const uniqueCompanies = res.data.filter((company: ICompany, index: number, self: ICompany[]) =>
            index === self.findIndex((c: ICompany) => c.id === company.id)
          );

          // Sort companies alphabetically by name
          this.companiesData = [...uniqueCompanies].sort((a, b) => a.name.localeCompare(b.name));
    
          // If only one company
          if (this.companiesData.length === 1) {
            if (this.companiesData[0].id) {
              this.selectedCompanyId = this.companiesData[0].id;
              this.reportForm.patchValue({ companySelector: this.selectedCompanyId });

            }
          }
        });

        if (!this.isCompanyOwner) {
          // Marketing Manager only
          this.appService.getAllCampaigns$()
            .pipe(takeUntil(this.destroyed$))
            .subscribe((response: {good:boolean; data:Array<any>; totalCount:number}) => {
              this.campaignsData = response.data;
            });
          this.appService.fetchAllCampaigns({});
        }
    } else {
      this.appService.getAllCompanies$()
      .pipe(takeUntil(this.destroyed$)
      )
      .subscribe((response: Array<ICompany>) => {
        // Remove duplicate companies based on id
        const uniqueCompanies = response.filter((company: ICompany, index: number, self: ICompany[]) =>
          index === self.findIndex((c: ICompany) => c.id === company.id)
        );

        // Sort companies alphabetically by name
        this.companiesData = [...uniqueCompanies].sort((a, b) => a.name.localeCompare(b.name));
        if (this.companiesData.length === 1 && this.companiesData[0].id) {
          this.selectedCompanyId = this.companiesData[0].id;
          this.reportForm.patchValue({ companySelector: this.selectedCompanyId });
        }
      });
      this.appService.fetchAllCompanies(null);
    }
  }

  private syncActivePlatformTab(): void {
    if (this.hasMetaPlatformData && this.hasGooglePlatformData) {
      return;
    }

    if (this.hasMetaPlatformData) {
      this.activeTab = 'meta';
      return;
    }

    if (this.hasGooglePlatformData) {
      this.activeTab = 'google';
      return;
    }

    this.activeTab = 'meta';
  }

  updateReport() {
    if (this.reportForm.invalid) {
      this.reportForm.markAllAsTouched(); // Mark all controls as touched to trigger validation
      return;
    }
    this.DateStart = this.reportForm.get('startDate')?.value;
    this.DateStop = this.reportForm.get('stopDate')?.value;
    this.setCustomPeriodForManualDates(this.DateStart, this.DateStop);
    this.selectedPeriod = this.reportForm.get('key')?.value;

    // Update selectedCompanyId from the form control value
    this.selectedCompanyId = this.reportForm.get('companySelector')?.value;

    this.loading = true;
    this.updateData(this.selectedCompanyId).finally(() => {
      this.loading = false;
    });
  }

  async updateData(companyId: string) {
    const company = this.companiesData.find(c => c.id === companyId);
    if (company && this.DateStart && this.DateStop) {
      const currentStartDate = new Date(this.DateStart);
      const currentEndDate = new Date(this.DateStop);
      const previousPeriod = this.getPreviousPeriodDates(currentStartDate, currentEndDate);

      try {
        const meta = await this.fetchDataJson('meta', company.name, currentStartDate.toISOString().split('T')[0], currentEndDate.toISOString().split('T')[0]);
        const previousMeta = await this.fetchDataJson('meta', company.name, previousPeriod.startDate, previousPeriod.endDate);
        const restrictCampaigns = this.isExternal && !this.isCompanyOwner;
        const previousMetaForMetrics = restrictCampaigns
          ? this.filterReportDataByCampaignAccess(previousMeta)
          : previousMeta;
        this.companyMetaData = [];
        this.companyMetaTotals = [];
        this.companyMetaAverages = [];

        if (restrictCampaigns) {
          // Marketing Manager or Owner viewing external report - only show campaigns they have access to
          for (const key in meta) {
            if (key === 'totals') {
              continue;
            } else if (key === 'averages') {
              continue;
            } else {
              // Only push if reach_campaign_id matches one of the ids in this.campaignsData
              if (this.isCampaignAccessible(meta[key])) {
                this.companyMetaData.push(meta[key]);
              }
            }
          }
          this.companyTempTotals = {
            cost: 0,
            leads: 0,
            impressions: 0,
            clicks: 0,
            cost_prev: 0,
            leads_prev: 0,
            impressions_prev: 0,
            clicks_prev: 0,
          };
          for (const key in this.companyMetaData) {
              const cost = Number(this.companyMetaData[key].cost) || 0;
              const leads = Number(this.companyMetaData[key].leads) || 0;
              const impressions = Number(this.companyMetaData[key].impressions) || 0;
              const clicks = Number(
                this.companyMetaData[key].clicks
                ?? this.companyMetaData[key].Clicks
                ?? this.companyMetaData[key].link_clicks
                ?? this.companyMetaData[key].linkClicks
              ) || 0;

              const costPrev = Number(this.companyMetaData[key].cost_prev ?? this.companyMetaData[key].costPrev) || 0;
              const leadsPrev = Number(this.companyMetaData[key].leads_prev ?? this.companyMetaData[key].leadsPrev) || 0;
              const impressionsPrev = Number(this.companyMetaData[key].impressions_prev ?? this.companyMetaData[key].impressionsPrev) || 0;
              const clicksPrev = Number(
                this.companyMetaData[key].clicks_prev
                ?? this.companyMetaData[key].clicksPrev
                ?? this.companyMetaData[key].link_clicks_prev
                ?? this.companyMetaData[key].linkClicksPrev
              ) || 0;

              this.companyTempTotals['cost'] += cost;
              this.companyTempTotals['leads'] += leads;
              this.companyTempTotals['impressions'] += impressions;
              this.companyTempTotals['clicks'] += clicks;
              this.companyTempTotals['cost_prev'] += costPrev;
              this.companyTempTotals['leads_prev'] += leadsPrev;
              this.companyTempTotals['impressions_prev'] += impressionsPrev;
              this.companyTempTotals['clicks_prev'] += clicksPrev;
          }
          this.companyMetaTotals = [{ location: 'Total', ...this.companyTempTotals }];
        } else {
          for (const key in meta) {
            if (key === 'totals') {
              this.companyMetaTotals.push(meta[key]);
            } else if (key === 'averages') {
              this.companyMetaAverages.push(meta[key]);
            } else {
              this.companyMetaData.push(meta[key]);
            }
          }
        }
        this.replaceAggregatePreviousMetrics(this.companyMetaTotals, this.companyMetaAverages, previousMetaForMetrics);
        this.syncActivePlatformTab();
        // console.log('this.companyMetaData', this.companyMetaData);
        // console.log('this.companyMetaTotals', this.companyMetaTotals);
        // console.log('this.companyMetaAverages', this.companyMetaAverages);

        const google = await this.fetchDataJson('google', company.name, currentStartDate.toISOString().split('T')[0], currentEndDate.toISOString().split('T')[0]);
        const previousGoogle = await this.fetchDataJson('google', company.name, previousPeriod.startDate, previousPeriod.endDate);
        const previousGoogleForMetrics = restrictCampaigns
          ? this.filterReportDataByCampaignAccess(previousGoogle)
          : previousGoogle;
        this.previousMetaCampaigns = this.countUniqueCampaigns(this.getReportRows(previousMetaForMetrics));
        this.previousGoogleCampaigns = this.countUniqueCampaigns(this.getReportRows(previousGoogleForMetrics));
        this.previousCombinedCampaigns = this.countUniqueCampaigns([
          ...this.getReportRows(previousMetaForMetrics),
          ...this.getReportRows(previousGoogleForMetrics),
        ]);
        this.companyGoogleData = [];
        this.companyGoogleTotals = [];
        this.companyGoogleAverages = [];

        if (restrictCampaigns) {
          // Marketing Manager or Owner viewing external report - only show campaigns they have access to
          for (const key in google) {
            if (key === 'totals') {
              continue;
            } else if (key === 'averages') {
              continue;
            } else {
              // Only push if reach_campaign_id matches one of the ids in this.campaignsData
              if (this.isCampaignAccessible(google[key])) {
                this.companyGoogleData.push(google[key]);
              }
            }
          }
          this.companyTempTotals = {
            cost: 0,
            leads: 0,
            impressions: 0,
            clicks: 0,
            cost_prev: 0,
            leads_prev: 0,
            impressions_prev: 0,
            clicks_prev: 0,
          };
          for (const key in this.companyGoogleData) {            
              const cost = Number(this.companyGoogleData[key].cost) || 0;
              const leads = Number(this.companyGoogleData[key].leads) || 0;
              const impressions = Number(this.companyGoogleData[key].impressions) || 0;
              const clicks = Number(
                this.companyGoogleData[key].clicks
                ?? this.companyGoogleData[key].Clicks
                ?? this.companyGoogleData[key].link_clicks
                ?? this.companyGoogleData[key].linkClicks
              ) || 0;

              const costPrev = Number(this.companyGoogleData[key].cost_prev ?? this.companyGoogleData[key].costPrev) || 0;
              const leadsPrev = Number(this.companyGoogleData[key].leads_prev ?? this.companyGoogleData[key].leadsPrev) || 0;
              const impressionsPrev = Number(this.companyGoogleData[key].impressions_prev ?? this.companyGoogleData[key].impressionsPrev) || 0;
              const clicksPrev = Number(
                this.companyGoogleData[key].clicks_prev
                ?? this.companyGoogleData[key].clicksPrev
                ?? this.companyGoogleData[key].link_clicks_prev
                ?? this.companyGoogleData[key].linkClicksPrev
              ) || 0;

              this.companyTempTotals['cost'] += cost;
              this.companyTempTotals['leads'] += leads;
              this.companyTempTotals['impressions'] += impressions;
              this.companyTempTotals['clicks'] += clicks;
              this.companyTempTotals['cost_prev'] += costPrev;
              this.companyTempTotals['leads_prev'] += leadsPrev;
              this.companyTempTotals['impressions_prev'] += impressionsPrev;
              this.companyTempTotals['clicks_prev'] += clicksPrev;
          }
          this.companyGoogleTotals = [{ location: 'Total', ...this.companyTempTotals }];
        } else {
          for (const key in google) {
            if (key === 'totals') {
              this.companyGoogleTotals.push(google[key]);
            } else if (key === 'averages') {
              this.companyGoogleAverages.push(google[key]);
            } else {
              this.companyGoogleData.push(google[key]);
            }
          }
        }
        this.replaceAggregatePreviousMetrics(this.companyGoogleTotals, this.companyGoogleAverages, previousGoogleForMetrics);
        this.initializeConversionActionSelections();
        this.applyConversionActionSelection();
        this.syncActivePlatformTab();
        this.rebuildPlatformColumns();
        // console.log('this.companyGoogleData', this.companyGoogleData);
        // console.log('this.companyGoogleTotals', this.companyGoogleTotals);
        // console.log('this.companyGoogleAverages', this.companyGoogleAverages);
      } catch (error) {
        console.error('Error updating data:', error);
      }
    }
  }

  private getReportRows(reportData: { [key: string]: any }): Array<{ [key: string]: any }> {
    return Object.keys(reportData ?? {})
      .filter(key => key !== 'totals' && key !== 'averages')
      .map(key => reportData[key])
      .filter(row => row && typeof row === 'object');
  }

  private isCampaignAccessible(row: { [key: string]: any }): boolean {
    const campaignId = row?.reach_campaign_id ?? row?.reachCampaignId ?? row?.campaignId ?? row?.id;
    return campaignId != null
      && (this.campaignsData ?? []).some(campaign => campaign?.id != null && String(campaign.id) === String(campaignId));
  }

  private filterReportDataByCampaignAccess(
    reportData: { [key: string]: any },
  ): { [key: string]: any } {
    const filteredData: { [key: string]: any } = {};
    for (const key of Object.keys(reportData ?? {})) {
      if (key !== 'totals' && key !== 'averages' && this.isCampaignAccessible(reportData[key])) {
        filteredData[key] = reportData[key];
      }
    }
    return filteredData;
  }

  private getPreviousPeriodDates(startDate: Date, endDate: Date): { startDate: string; endDate: string } {
    const rangeInDays = Math.round((endDate.getTime() - startDate.getTime()) / 86400000) + 1;
    const previousEndDate = new Date(startDate);
    previousEndDate.setUTCDate(previousEndDate.getUTCDate() - 1);
    const previousStartDate = new Date(previousEndDate);
    previousStartDate.setUTCDate(previousStartDate.getUTCDate() - rangeInDays + 1);

    return {
      startDate: previousStartDate.toISOString().split('T')[0],
      endDate: previousEndDate.toISOString().split('T')[0],
    };
  }

  private replaceAggregatePreviousMetrics(
    totals: Array<{ [key: string]: any }>,
    averages: Array<{ [key: string]: any }>,
    previousData: { [key: string]: any },
  ): void {
    const previousRows = Object.keys(previousData ?? {})
      .filter(key => key !== 'totals' && key !== 'averages')
      .map(key => previousData[key])
      .filter(row => row && typeof row === 'object');
    const previousTotals = { ...(previousData?.totals ?? {}) };

    for (const metric of ['cost', 'leads', 'impressions', 'clicks']) {
      if (!(metric in previousTotals)) {
        previousTotals[metric] = previousRows.reduce((sum, row) => sum + this.getRowMetric(row, metric), 0);
      }
    }

    this.replaceAggregateRowPreviousMetrics(totals[0], previousTotals);
    this.replaceAggregateRowPreviousMetrics(averages[0], previousData?.averages);
  }

  private getRowMetric(row: { [key: string]: any }, metric: string): number {
    const aliases: { [key: string]: string[] } = {
      clicks: ['clicks', 'Clicks', 'link_clicks', 'linkClicks'],
      leads: ['leads', 'Leads'],
      impressions: ['impressions', 'Impressions'],
      cost: ['cost', 'Cost'],
    };
    const key = (aliases[metric] ?? [metric]).find(candidate => candidate in row);
    return key ? this.toNumber(row[key]) : 0;
  }

  private replaceAggregateRowPreviousMetrics(
    currentRow: { [key: string]: any } | undefined,
    previousRow: { [key: string]: any } | undefined,
  ): void {
    if (!currentRow || !previousRow) {
      return;
    }

    for (const key of Object.keys(previousRow)) {
      if (key.endsWith('_prev')) {
        const currentKey = key.slice(0, -5);
        if (currentKey in previousRow) {
          currentRow[key] = previousRow[currentKey];
        }
      } else if (`${key}_prev` in currentRow || ['cost', 'leads', 'impressions', 'clicks'].includes(key)) {
        currentRow[`${key}_prev`] = previousRow[key];
      }
    }

    for (const key of Object.keys(currentRow)) {
      if (!key.endsWith('_compare')) {
        continue;
      }

      const currentKey = key.slice(0, -8);
      const previousKey = `${currentKey}_prev`;
      if (currentKey in currentRow && previousKey in currentRow) {
        const currentValue = Number(currentRow[currentKey]) || 0;
        const previousValue = Number(currentRow[previousKey]) || 0;
        currentRow[key] = previousValue === 0 ? (currentValue === 0 ? 0 : null) : (currentValue - previousValue) / Math.abs(previousValue);
      }
    }
  }

  fetchDataJson(platform: string, companyName: string, startDate: string, endDate: string): Promise<{ [key: string]: string }[]> {
    let url = `https://dms.reach.marketing/dmm/cmo-reach.php?company=${companyName}&start-date=${startDate}&end-date=${endDate}&platform=${platform}`;
    if (this.instance.isAltLayout) {
      url = `https://dms.reach.marketing/dmm/alt/brand-report.php?company=${companyName}&start-date=${startDate}&end-date=${endDate}&platform=${platform}`;
    } 
    
    return fetch(url)
      .then(response => response.json())
      .then(data => {
        // const arrayData = Object.keys(data).map(key => data[key]);
        // console.log('Array data:', arrayData);
        return data;
      })
      .catch((error): { [key: string]: string }[] => {
        console.error('Error fetching JSON data:', error);
        return [];
      });
  }

  getActiveCompanyName(): string {
    const companyId = this.reportForm?.get('companySelector')?.value ?? this.selectedCompanyId;
    const company = this.companiesData.find(c => c.id === companyId);
    const name = company?.name?.trim();
    return name ?? '';
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
